import {
  type DateRange,
  DateRangeCalendar,
  type DateRangePickerProps,
  PickersShortcuts,
} from "@mui/x-date-pickers-pro";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import * as styles from "./DateRangePickerInput.style.ts";
import React, {
  forwardRef,
  type ReactNode,
  type Ref,
  useId,
  useState,
} from "react";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { InputErrorMessage } from "src/components/InputErrorMessage/InputErrorMessage.tsx";
import { type Dayjs, default as dayjs } from "dayjs";
import type { PickersShortcutsItem } from "@mui/x-date-pickers/PickersShortcuts/PickersShortcuts";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { usePickersTranslations } from "@mui/x-date-pickers/hooks";
import { loadTranslations } from "src/lib/i18next/loadTranslations.ts";
import { SingleInputDateRangeField } from "src/components/DateRangePicker/SingleInputDateRangeField.tsx";
import {
  FloatingOverlay,
  FloatingPortal,
  shift,
  useFloating,
} from "@floating-ui/react";
import { FLOATING_PORTAL_ROOT_ID } from "src/common/constants.ts";
import { Flex } from "src/components/Flex.tsx";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { css } from "@emotion/react";
import { createTheme } from "@mui/material";
import { fontStack } from "src/lib/emotion/utils.ts";

const muiTheme = createTheme({
  typography: {
    fontFamily: fontStack(["Poppins", "sans-serif"]),
  },
});

type Value = [null | Dayjs, null | Dayjs];

export type DateRangePickerInputProps = Omit<
  DateRangePickerProps<any>,
  "onChange"
> & {
  label?: string;
  required?: boolean;
  onChange?: (value: Value) => void;
  helperText?: ReactNode;
  error?: string;
  isLoading?: boolean;
  floatingError?: boolean;
  onClear?: () => void;
  shortcuts?: PickersShortcutsItem<
    [string | null | Dayjs, string | null | Dayjs]
  >[];
};
await loadTranslations(TranslationNamespaceEnum.DateRangePicker);

export const DateRangePickerInput = forwardRef(
  (
    {
      label,
      required,
      helperText,
      isLoading = false,
      disabled,
      floatingError = false,
      onChange,
      shortcuts: shortcutsProp,
      error,
      slotProps: slotPropsProp,
      onClear,
      value,
      ...rest
    }: DateRangePickerInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    const [open, setOpen] = useState(false);

    const { t } = useTranslation(TranslationNamespaceEnum.DateRangePicker);
    const setOpenCalendar = setOpen;
    const { floatingStyles, refs } = useFloating({
      placement: "bottom-start",
      onOpenChange: (open) => {
        setOpenCalendar(open);
      },
      middleware: [
        shift({
          crossAxis: true,
          padding: 10,
        }),
      ],
    });

    const pickerTranslations = usePickersTranslations();
    const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
      {
        label: t("shortcuts.thisWeek"),
        getValue: () => {
          const today = dayjs();
          return [today.startOf("week"), today.endOf("week")];
        },
      },
      {
        label: t("shortcuts.lastWeek"),
        getValue: () => {
          const today = dayjs();
          const prevWeek = today.subtract(7, "day");
          return [prevWeek.startOf("week"), prevWeek.endOf("week")];
        },
      },
      {
        label: t("shortcuts.lastSevenDays"),
        getValue: () => {
          const today = dayjs();
          return [today.subtract(7, "day"), today];
        },
      },
      {
        label: t("shortcuts.currentMonth"),
        getValue: () => {
          const today = dayjs();
          return [today.startOf("month"), today.endOf("month")];
        },
      },
      {
        label: t("shortcuts.lastMonth"),
        getValue: () => {
          const today = dayjs();
          const startOfPrevMonth = today.subtract(1, "month").startOf("month");
          return [startOfPrevMonth, startOfPrevMonth.endOf("month")];
        },
      },
      {
        label: pickerTranslations.clearButtonLabel,
        getValue: () => [null, null],
      },
    ];

    const handleChange = (range: DateRange<any>) => {
      onChange?.(range);
    };

    const handleCalendarClick = () => {
      if (!disabled) {
        setOpen(true);
      }
    };

    const handleClearClick = () => {
      if (!disabled) {
        onClear?.();
      }
    };

    const id = useId();
    return (
      <Skeleton isLoading={isLoading}>
        <div css={styles.root}>
          {label && (
            <InputLabel
              id={id}
              label={label}
              required={required}
              helperText={helperText}
            />
          )}
          <SingleInputDateRangeField
            css={styles.input}
            onCalendarClick={handleCalendarClick}
            onClearClick={handleClearClick}
            inputRef={refs.setReference}
            onChange={onChange}
            disabled={disabled}
            style={{
              width: "100%",
              height: "100%",
            }}
            slotProps={{
              textField: {
                error: !!error,
              },
            }}
            clearable={true}
            value={value}
          />
          {open && (
            <MuiThemeProvider theme={muiTheme}>
              <FloatingPortal id={FLOATING_PORTAL_ROOT_ID}>
                <FloatingOverlay
                  onClick={() => {
                    setOpenCalendar(false);
                  }}
                  css={floatingCalendarOverlayStyles}
                />
                <div
                  ref={refs.setFloating}
                  css={floatingCalendarContainerStyles}
                  style={floatingStyles}
                >
                  <Flex>
                    <PickersShortcuts
                      isValid={() => true}
                      items={shortcutsProp ?? shortcutsItems}
                      isLandscape={false}
                      onChange={handleChange}
                    />
                    <DateRangeCalendar onChange={handleChange} value={value} />
                  </Flex>
                </div>
              </FloatingPortal>
            </MuiThemeProvider>
          )}

          {!!error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    );
  },
);

const floatingCalendarOverlayStyles = (theme: ExactTheme) => css`
  z-index: ${theme.zIndices.popovers};
`;
const floatingCalendarContainerStyles = (theme: ExactTheme) => css`
  background: white;
  border-radius: 6px;
  box-shadow: ${theme.shadowV2.tertiary};
  z-index: ${theme.zIndices.popovers + 1};
`;
